// Kanban « Mes tâches » (LOT 1) — vue Statuts.
// Outil PERSO admin. AUCUN lien menage_runs / checklists.
//
// - 3 colonnes fixes (À faire / En cours / Fait).
// - Drag @dnd-kit entre colonnes = change status (optimistic + rollback + toast).
// - CRUD admin_tasks + task_labels (client direct, RLS admin_all).
// - Filtres scope / logement / étiquette.
// - Sélecteur de vue (Statuts actif ; Par logement / Swimlanes désactivés Lot 2).
// - Gestionnaire d'étiquettes + Archives + suppression définitive.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { DndContext, closestCorners, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  Plus, Tag, Archive, Filter, X, ClipboardList,
  LayoutDashboard, Building2, Rows3, Calendar, Home, User, Pin,
} from 'lucide-react'
import { supabase } from '../../supabase'
import { DS } from '../../lib/ui'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import {
  STATUSES,
  STATUS_LABELS,
  SCOPES,
  SCOPE_LABELS,
  DUE_BUCKETS,
  DUE_BUCKET_LABELS,
  isValidStatus,
  groupBy,
  applyFilters,
  tasksAffectedByLabelRemoval,
  buildRows,
  patchForLogementDrop,
  patchForSwimlaneDrop,
  patchForEcheanceDrop,
  dueBucket,
} from './taches-logic'
import { ECHEANCE_DOT, COLUMN_BG, stripLeadEmoji } from './taches-ui'
import TaskColumn from './TaskColumn'
import TaskDropColumn from './TaskDropColumn'
import SwimlaneGrid from './SwimlaneGrid'
import TaskModal from './TaskModal'
import LabelManager from './LabelManager'

// Icône de tête d'une colonne « Par logement » (présentation).
function rowHeaderIcon(row) {
  if (row.kind === 'logement') return <Home size={15} strokeWidth={2} />
  if (row.scope === 'entreprise') return <Building2 size={15} strokeWidth={2} />
  if (row.scope === 'proprio') return <User size={15} strokeWidth={2} />
  return <Pin size={15} strokeWidth={2} />
}

// Vues commutables (icône lucide + libellé).
const VIEW_DEFS = [
  { id: 'status', label: 'Statuts', Icon: LayoutDashboard },
  { id: 'logement', label: 'Par logement', Icon: Building2 },
  { id: 'swimlane', label: 'Swimlanes', Icon: Rows3 },
  { id: 'echeance', label: 'Par échéance', Icon: Calendar },
]

export default function MesTachesKanban({ cfg }) {
  const [tasks, setTasks] = useState([])
  const [labels, setLabels] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [toast, setToast] = useState(null) // { type:'error'|'ok', msg }

  const [modalTask, setModalTask] = useState(null)   // task object or {} for new
  const [showLabels, setShowLabels] = useState(false)
  const [showArchives, setShowArchives] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null) // task to delete

  const [view, setView] = useState('status') // Lot 1 : seul 'status' actif
  const [fScope, setFScope] = useState('')
  const [fLog, setFLog] = useState('')
  const [fLabel, setFLabel] = useState('')
  const [showFilters, setShowFilters] = useState(false) // filtres repliés derrière le bouton

  // Anti-double-commit drag (React Strict Mode double-invoque les handlers).
  const isDraggingRef = useRef(false)

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg })
    setTimeout(() => setToast(null), 3500)
  }, [])

  // ── Chargement ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    const [tRes, lRes] = await Promise.all([
      supabase.from('admin_tasks').select('*').order('position', { ascending: true }),
      supabase.from('task_labels').select('*').order('position', { ascending: true }),
    ])
    if (tRes.error) {
      console.error('[MesTaches] load tasks', tRes.error)
      setLoadError(tRes.error.message)
      setLoading(false)
      return
    }
    if (lRes.error) {
      console.error('[MesTaches] load labels', lRes.error)
    }
    setTasks(tRes.data || [])
    setLabels(lRes.data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const labelsById = useMemo(() => {
    const m = {}
    for (const l of labels) m[l.id] = l
    return m
  }, [labels])

  // Logements pour le filtre (NOM résolu).
  const logsForFilter = useMemo(() => getAllLogsFlat(cfg), [cfg])

  // ── Tâches actives filtrées (hors archivées) ──────────────────────
  const activeTasks = useMemo(
    () => applyFilters(tasks, { scope: fScope, logementId: fLog, labelId: fLabel, archived: false }),
    [tasks, fScope, fLog, fLabel]
  )
  const archivedTasks = useMemo(
    () => applyFilters(tasks, { archived: true }),
    [tasks]
  )

  // Repère vivant (présentation seule) : tâches ouvertes + échéant aujourd'hui.
  const openCount = useMemo(() => activeTasks.filter(t => t.status !== 'done').length, [activeTasks])
  const todayCount = useMemo(
    () => activeTasks.filter(t => t.status !== 'done' && dueBucket(t.due_at) === 'today').length,
    [activeTasks]
  )
  const activeFilterCount = (fScope ? 1 : 0) + (fLog ? 1 : 0) + (fLabel ? 1 : 0)

  const grouped = useMemo(() => groupBy(activeTasks, view), [activeTasks, view])
  // Lignes (logements + colonnes spéciales) pour les vues logement & swimlane.
  const rows = useMemo(() => buildRows(cfg), [cfg])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  )

  // ── Cohérence done_at quand un patch touche au statut ─────────────
  // Posé en passant à done, effacé sinon. Centralisé pour que TOUTES les
  // vues (statut, swimlane) qui changent le statut restent cohérentes.
  const withDoneAt = useCallback((patch) => {
    if (!('status' in patch)) return patch
    return { ...patch, done_at: patch.status === 'done' ? new Date().toISOString() : null }
  }, [])

  // ── Écriture optimiste + rollback + toast (mutualisée drag toutes vues) ─
  // `patch` ne contient QUE les champs à écrire ; le rollback restaure les
  // valeurs antérieures de ces mêmes champs depuis l'objet courant.
  const applyDragPatch = useCallback(async (taskId, rawPatch) => {
    const current = tasks.find(t => t.id === taskId)
    if (!current || !rawPatch) return
    const patch = withDoneAt(rawPatch)

    const prev = {}
    for (const k of Object.keys(patch)) prev[k] = current[k]

    setTasks(list => list.map(t => (t.id === taskId ? { ...t, ...patch } : t)))

    const { error } = await supabase.from('admin_tasks').update(patch).eq('id', taskId).select()
    if (error) {
      console.error('[MesTaches] drag update', error)
      setTasks(list => list.map(t => (t.id === taskId ? { ...t, ...prev } : t)))
      showToast('error', 'Déplacement non enregistré — réessaie.')
    }
  }, [tasks, withDoneAt, showToast])

  // ── Drag : sémantique par vue (patch résolu en pur, cf. taches-logic) ──
  const handleDragStart = () => { isDraggingRef.current = true }
  const handleDragCancel = () => { isDraggingRef.current = false }

  const handleDragEnd = useCallback(async (event) => {
    if (!isDraggingRef.current) return // anti-double-commit (Strict Mode)
    isDraggingRef.current = false

    const { active, over } = event
    if (!over) return
    const taskId = active.id
    const overKey = over.id
    const current = tasks.find(t => t.id === taskId)
    if (!current) return

    let patch = null
    if (view === 'status') {
      // Vue Statuts — drop sur une colonne statut (whitelist anti-écriture).
      if (!isValidStatus(overKey)) {
        console.warn('[MesTaches] drop sur statut hors whitelist:', overKey)
        return
      }
      if (current.status === overKey) return
      patch = { status: overKey }
    } else if (view === 'logement') {
      patch = patchForLogementDrop(current, overKey, cfg)
    } else if (view === 'swimlane') {
      patch = patchForSwimlaneDrop(current, overKey, cfg)
    } else if (view === 'echeance') {
      patch = patchForEcheanceDrop(current, overKey)
    }
    if (!patch) return

    await applyDragPatch(taskId, patch)
  }, [tasks, view, cfg, applyDragPatch])

  // ── CRUD tâche ────────────────────────────────────────────────────
  const saveTask = useCallback(async (id, patch) => {
    if (id) {
      const { data, error } = await supabase.from('admin_tasks').update(patch).eq('id', id).select().single()
      if (error) {
        console.error('[MesTaches] update task', error)
        showToast('error', 'Enregistrement échoué.')
        return false
      }
      setTasks(prev => prev.map(t => (t.id === id ? data : t)))
      return true
    }
    // Création : position en fin de colonne todo.
    const maxPos = tasks.reduce((m, t) => Math.max(m, t.position || 0), 0)
    const insert = { ...patch, status: 'todo', position: maxPos + 1 }
    const { data, error } = await supabase.from('admin_tasks').insert(insert).select().single()
    if (error) {
      console.error('[MesTaches] insert task', error)
      showToast('error', 'Création échouée.')
      return false
    }
    setTasks(prev => [...prev, data])
    return true
  }, [tasks, showToast])

  const toggleDone = useCallback(async (task) => {
    const toDone = task.status !== 'done'
    const patch = {
      status: toDone ? 'done' : 'todo',
      done_at: toDone ? new Date().toISOString() : null,
    }
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...patch } : t)))
    const { error } = await supabase.from('admin_tasks').update(patch).eq('id', task.id).select()
    if (error) {
      console.error('[MesTaches] toggle done', error)
      setTasks(prev => prev.map(t => (t.id === task.id ? task : t)))
      showToast('error', 'Mise à jour échouée.')
    }
  }, [showToast])

  const archiveTask = useCallback(async (task) => {
    const patch = { archived_at: new Date().toISOString() }
    const { error } = await supabase.from('admin_tasks').update(patch).eq('id', task.id).select()
    if (error) {
      console.error('[MesTaches] archive', error)
      showToast('error', 'Archivage échoué.')
      return
    }
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...patch } : t)))
    setModalTask(null)
  }, [showToast])

  const unarchiveTask = useCallback(async (task) => {
    const { error } = await supabase.from('admin_tasks').update({ archived_at: null }).eq('id', task.id).select()
    if (error) {
      console.error('[MesTaches] unarchive', error)
      showToast('error', 'Restauration échouée.')
      return
    }
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, archived_at: null } : t)))
  }, [showToast])

  const deleteTask = useCallback(async (task) => {
    const { error } = await supabase.from('admin_tasks').delete().eq('id', task.id)
    if (error) {
      console.error('[MesTaches] delete', error)
      showToast('error', 'Suppression échouée.')
      return
    }
    setTasks(prev => prev.filter(t => t.id !== task.id))
    setConfirmDelete(null)
    setModalTask(null)
  }, [showToast])

  // ── CRUD étiquettes ───────────────────────────────────────────────
  const createLabel = useCallback(async (patch) => {
    const maxPos = labels.reduce((m, l) => Math.max(m, l.position || 0), 0)
    const { data, error } = await supabase.from('task_labels').insert({ ...patch, position: maxPos + 1 }).select().single()
    if (error) {
      console.error('[MesTaches] create label', error)
      showToast('error', 'Création étiquette échouée.')
      return false
    }
    setLabels(prev => [...prev, data])
    return true
  }, [labels, showToast])

  const updateLabel = useCallback(async (id, patch) => {
    const { data, error } = await supabase.from('task_labels').update(patch).eq('id', id).select().single()
    if (error) {
      console.error('[MesTaches] update label', error)
      showToast('error', 'Modification étiquette échouée.')
      return false
    }
    setLabels(prev => prev.map(l => (l.id === id ? data : l)))
    return true
  }, [showToast])

  const deleteLabel = useCallback(async (label) => {
    // 1) Retirer le label des tâches qui le portent (admin_tasks.labels).
    const affected = tasksAffectedByLabelRemoval(tasks, label.id)
    for (const a of affected) {
      const { error } = await supabase.from('admin_tasks').update({ labels: a.labels }).eq('id', a.id).select()
      if (error) {
        console.error('[MesTaches] strip label from task', error)
        showToast('error', 'Nettoyage des tâches échoué — étiquette conservée.')
        return
      }
    }
    // 2) Supprimer l'étiquette.
    const { error } = await supabase.from('task_labels').delete().eq('id', label.id)
    if (error) {
      console.error('[MesTaches] delete label', error)
      showToast('error', 'Suppression étiquette échouée.')
      return
    }
    setLabels(prev => prev.filter(l => l.id !== label.id))
    setTasks(prev => prev.map(t => (
      Array.isArray(t.labels) && t.labels.includes(label.id)
        ? { ...t, labels: t.labels.filter(x => x !== label.id) }
        : t
    )))
    if (fLabel === label.id) setFLabel('')
  }, [tasks, fLabel, showToast])

  // ── Rendu ─────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 24, color: DS.textMuted }}>Chargement…</div>
  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: DS.danger, fontWeight: 600, marginBottom: 8 }}>Erreur de chargement</div>
        <div style={{ fontSize: 13, color: DS.textSecondary, marginBottom: 12 }}>{loadError}</div>
        <button type="button" onClick={load} style={ctaBtn}>Réessayer</button>
      </div>
    )
  }

  return (
    <div>
      {/* ── Barre de contrôle (2 lignes, rangée) ────────────────────────── */}
      {/* Ligne 1 : utilitaires sobres + action unique (corail). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div style={{ flex: 1 }} />
        <button type="button" className="fbz-press fbz-tap" onClick={() => setShowLabels(true)} style={utilityBtn} data-testid="open-labels">
          <Tag size={15} strokeWidth={2} /> Étiquettes
        </button>
        <button type="button" className="fbz-press fbz-tap" onClick={() => setShowArchives(s => !s)} style={utilityBtn} data-testid="toggle-archives">
          <Archive size={15} strokeWidth={2} /> Archives{archivedTasks.length ? ` (${archivedTasks.length})` : ''}
        </button>
        <button type="button" className="fbz-press fbz-tap" onClick={() => setModalTask({})} style={ctaBtn} data-testid="task-add">
          <Plus size={16} strokeWidth={2.2} /> Nouvelle tâche
        </button>
      </div>

      {/* Ligne 2 : titre + repère vivant · sélecteur de vue + bouton Filtres. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 22, color: DS.marineDark, lineHeight: 1.1 }}>
            Mes tâches
          </div>
          <div style={{ fontSize: 12.5, color: DS.textSecondary, marginTop: 3 }}>
            {openCount} ouverte{openCount > 1 ? 's' : ''}
            {' · '}
            <span style={{ color: DS.corail, fontWeight: 600 }}>{todayCount} pour aujourd'hui</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {/* Sélecteur de vue — pilule active = fond ciel (choix d'affichage). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 10, padding: 3 }}>
            {VIEW_DEFS.map(v => {
              const on = view === v.id
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setView(v.id)}
                  data-testid={`view-${v.id}`}
                  title={v.label}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 12.5, fontWeight: 600, padding: '6px 11px', borderRadius: 8, border: 'none',
                    cursor: 'pointer',
                    background: on ? DS.cielSoft : 'transparent',
                    color: on ? DS.marine : DS.textSecondary,
                  }}
                >
                  <v.Icon size={15} strokeWidth={2} /> {v.label}
                </button>
              )
            })}
          </div>

          {/* Bouton Filtres (compteur si actifs). */}
          <button
            type="button"
            className="fbz-press fbz-tap"
            onClick={() => setShowFilters(s => !s)}
            style={{ ...utilityBtn, ...(showFilters || activeFilterCount ? { borderColor: DS.borderHover, color: DS.marine } : {}) }}
            data-testid="toggle-filters"
          >
            <Filter size={15} strokeWidth={2} /> Filtres
            {activeFilterCount > 0 && (
              <span style={{ background: DS.corail, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 999, minWidth: 17, height: 17, padding: '0 5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Panneau de filtres — replié derrière le bouton. */}
      {showFilters && (
        <div
          style={{
            background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 12,
            padding: 16, marginBottom: 12, boxShadow: '0 8px 24px rgba(33,83,135,.10)',
            display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end',
          }}
        >
          <FilterField label="Contexte">
            <select value={fScope} onChange={e => setFScope(e.target.value)} style={filterStyle} data-testid="filter-scope">
              <option value="">Tous contextes</option>
              {SCOPES.map(s => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
            </select>
          </FilterField>
          <FilterField label="Logement">
            <select value={fLog} onChange={e => setFLog(e.target.value)} style={filterStyle} data-testid="filter-logement">
              <option value="">Tous logements</option>
              {logsForFilter.map(l => <option key={l.id} value={l.id}>{l.nom}</option>)}
            </select>
          </FilterField>
          <FilterField label="Étiquette">
            <select value={fLabel} onChange={e => setFLabel(e.target.value)} style={filterStyle} data-testid="filter-label">
              <option value="">Toutes étiquettes</option>
              {labels.map(l => <option key={l.id} value={l.id}>{l.emoji ? `${l.emoji} ` : ''}{l.name}</option>)}
            </select>
          </FilterField>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => { setFScope(''); setFLog(''); setFLabel('') }}
              style={{ background: 'none', border: 'none', color: DS.textSecondary, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontFamily: DS.fontBody }}
            >
              Réinitialiser
            </button>
            <button type="button" className="fbz-press" onClick={() => setShowFilters(false)} style={ctaBtn}>
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Chips de filtres actifs. */}
      {activeFilterCount > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {fScope && <FilterChip label={SCOPE_LABELS[fScope] || fScope} onClear={() => setFScope('')} />}
          {fLog && <FilterChip label={(logsForFilter.find(l => l.id === fLog) || {}).nom || fLog} onClear={() => setFLog('')} />}
          {fLabel && <FilterChip label={stripLeadEmoji((labels.find(l => l.id === fLabel) || {}).name || fLabel)} onClear={() => setFLabel('')} />}
        </div>
      )}

      {/* État vide chaleureux (board réellement vide, sans filtre). */}
      {activeTasks.length === 0 && activeFilterCount === 0 ? (
        <div style={{ textAlign: 'center', padding: '54px 20px', color: DS.textSecondary }}>
          <span
            style={{
              width: 56, height: 56, borderRadius: 16, background: DS.cielSoft, color: DS.marine,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
            }}
          >
            <ClipboardList size={26} strokeWidth={1.9} />
          </span>
          <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 18, color: DS.marineDark }}>
            Aucune tâche pour l'instant
          </div>
          <div style={{ fontSize: 13.5, marginTop: 6, marginBottom: 18 }}>
            Note ce qu'il y a à faire — par logement, par proprio ou pour l'entreprise.
          </div>
          <button type="button" className="fbz-press" onClick={() => setModalTask({})} style={ctaBtn}>
            <Plus size={16} strokeWidth={2.2} /> Nouvelle tâche
          </button>
        </div>
      ) : activeTasks.length === 0 ? (
        <div style={{ padding: '32px 16px', textAlign: 'center', fontSize: 13.5, color: DS.textMuted, fontStyle: 'italic' }}>
          Aucune tâche pour ces filtres.{' '}
          <button
            type="button"
            onClick={() => { setFScope(''); setFLog(''); setFLabel('') }}
            style={{ background: 'none', border: 'none', color: DS.marine, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontFamily: DS.fontBody, fontSize: 13.5 }}
          >
            Réinitialiser
          </button>
        </div>
      ) : (
      /* Kanban — 4 vues commutables sur la même donnée (admin_tasks). */
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* VUE STATUTS (Lot 1) */}
        {view === 'status' && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
            {STATUSES.map(st => (
              <TaskColumn
                key={st}
                status={st}
                label={STATUS_LABELS[st]}
                tasks={grouped[st] || []}
                cfg={cfg}
                labelsById={labelsById}
                onEdit={setModalTask}
                onToggleDone={toggleDone}
              />
            ))}
          </div>
        )}

        {/* VUE PAR LOGEMENT : 1 colonne par logement + colonnes spéciales scope */}
        {view === 'logement' && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
            {rows.map(row => (
              <TaskDropColumn
                key={row.key}
                dropId={row.key}
                label={stripLeadEmoji(row.label)}
                headerIcon={rowHeaderIcon(row)}
                accentColor={DS.ciel}
                tasks={grouped[row.key] || []}
                cfg={cfg}
                labelsById={labelsById}
                onEdit={setModalTask}
                onToggleDone={toggleDone}
              />
            ))}
          </div>
        )}

        {/* VUE SWIMLANES : grille logements × statuts */}
        {view === 'swimlane' && (
          <SwimlaneGrid
            rows={rows}
            grouped={grouped}
            cfg={cfg}
            labelsById={labelsById}
            onEdit={setModalTask}
            onToggleDone={toggleDone}
          />
        )}

        {/* VUE PAR ÉCHÉANCE : buckets agenda (Europe/Paris) */}
        {view === 'echeance' && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
            {DUE_BUCKETS.map(b => (
              <TaskDropColumn
                key={b}
                dropId={b}
                label={DUE_BUCKET_LABELS[b]}
                accentColor={ECHEANCE_DOT[b]}
                tasks={grouped[b] || []}
                cfg={cfg}
                labelsById={labelsById}
                onEdit={setModalTask}
                onToggleDone={toggleDone}
              />
            ))}
          </div>
        )}
      </DndContext>
      )}

      {/* Archives */}
      {showArchives && (
        <div style={{ marginTop: 20, background: COLUMN_BG, borderRadius: 14, padding: 16, border: `0.5px solid ${DS.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 15, color: DS.marineDark, marginBottom: 10 }}>
            <Archive size={16} strokeWidth={2} /> Archives ({archivedTasks.length})
          </div>
          {archivedTasks.length === 0 && <div style={{ fontSize: 13, color: DS.textMuted }}>Aucune tâche archivée.</div>}
          {archivedTasks.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', background: DS.card, borderRadius: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: DS.textSecondary, textDecoration: 'line-through', flex: 1, minWidth: 0 }}>{t.title}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={() => unarchiveTask(t)} style={miniBtn} data-testid="task-unarchive">Restaurer</button>
                <button type="button" onClick={() => setConfirmDelete(t)} style={{ ...miniBtn, color: DS.danger }}>Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modales */}
      {modalTask && (
        <TaskModal
          task={modalTask.id ? modalTask : null}
          cfg={cfg}
          labels={labels}
          onSave={saveTask}
          onClose={() => setModalTask(null)}
          onArchive={archiveTask}
          onDelete={(t) => setConfirmDelete(t)}
        />
      )}

      {showLabels && (
        <LabelManager
          labels={labels}
          onCreate={createLabel}
          onUpdate={updateLabel}
          onDelete={deleteLabel}
          onClose={() => setShowLabels(false)}
        />
      )}

      {/* Confirmation suppression définitive */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setConfirmDelete(null)}>
          <div onClick={e => e.stopPropagation()} className="fbz-modal-sheet" style={{ background: DS.card, borderRadius: 16, padding: 24, maxWidth: 360, width: '100%', boxShadow: '0 16px 48px rgba(0,0,0,.22)', fontFamily: DS.fontBody }}>
            <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 17, color: DS.danger, marginBottom: 8 }}>Supprimer définitivement ?</div>
            <div style={{ fontSize: 13, color: DS.textSecondary, marginBottom: 18 }}>« {confirmDelete.title} » sera supprimée sans retour possible.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={ghostBtn}>Annuler</button>
              <button type="button" onClick={() => deleteTask(confirmDelete)} style={{ ...ctaBtn, background: DS.danger }} data-testid="confirm-delete">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1100, background: toast.type === 'error' ? DS.danger : DS.success, color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,.25)' }} role="alert">
          {toast.msg}
        </div>
      )}
    </div>
  )
}

// ── Champ de filtre (label + contrôle) ───────────────────────────────
function FilterField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 150 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: DS.textSecondary, fontFamily: DS.fontBody }}>{label}</span>
      {children}
    </div>
  )
}

// ── Chip de filtre actif (✕ = retire ce filtre) ──────────────────────
function FilterChip({ label, onClear }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: DS.marine, background: DS.cielSoft, borderRadius: 999, padding: '4px 6px 4px 11px' }}>
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Retirer le filtre ${label}`}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', border: 'none', background: 'rgba(33,83,135,.12)', color: DS.marine, cursor: 'pointer', padding: 0 }}
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </span>
  )
}

const ctaBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '9px 15px', borderRadius: DS.radiusControl, border: 'none', background: DS.corail, color: '#fff', cursor: 'pointer', fontFamily: DS.fontBody }
const utilityBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '9px 14px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: 'pointer', fontFamily: DS.fontBody }
const ghostBtn = { fontSize: 13, fontWeight: 600, padding: '9px 14px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: 'pointer', fontFamily: DS.fontBody }
const miniBtn = { fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6, border: `0.5px solid ${DS.border}`, background: DS.card, color: DS.marine, cursor: 'pointer', fontFamily: DS.fontBody }
const filterStyle = { padding: '8px 10px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, fontSize: 13, fontFamily: DS.fontBody, background: DS.card, color: DS.marine, minWidth: 150 }
